import { addMessage } from '../../../actions/messagesActions';
import { loginSubmit } from '../../../actions/userActions';
import Loading from '../../common/Loading';
import Messages from '../Messages';
import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

const RECAPTCHA_ACTION = 'login';

// Resolve a reCAPTCHA Enterprise token at submit time. There is no widget for
// the user to interact with - a fresh token is minted each time the form is
// submitted and the backend rejects low-score or wrong-action responses.
const fetchRecaptchaToken = () =>
  new Promise((resolve, reject) => {
    const siteKey = window.RECAPTCHA_SITE_KEY;
    const enterprise = window.grecaptcha?.enterprise;
    if (!siteKey || !enterprise || typeof enterprise.ready !== 'function') {
      reject(new Error('reCAPTCHA is not available'));
      return;
    }
    enterprise.ready(() => {
      enterprise.execute(siteKey, { action: RECAPTCHA_ACTION }).then(resolve, reject);
    });
  });

const LoginForm = ({ content, loginSubmit, addMessage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const flashDanger = location.state?.flashDanger;
    if (!flashDanger) {
      return;
    }
    addMessage(flashDanger, 'danger');
    navigate('.', { replace: true, state: {} });
  }, [location.state, addMessage, navigate]);

  const getParameterByName = (name, url = window.location.href) => {
    const escaped = name.replace(/\[/g, '\\$&').replace(/\]/g, '\\$&');
    const regex = new RegExp('[?&]' + escaped + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const recaptchaToken = await fetchRecaptchaToken();
      const result = await loginSubmit({ ...values, recaptchaToken });
      if (result?.code_2fa_required) {
        const redirectAfterLogin = getParameterByName('redirect');
        const next = redirectAfterLogin
          ? `/login-code-verification?redirect=${encodeURIComponent(redirectAfterLogin)}`
          : '/login-code-verification';
        navigate(next, { state: { email: values.email } });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const redirect = getParameterByName('redirect');
  const r = getParameterByName('r');

  if (redirect && r !== '0') {
    const match = redirect.match(/^\/start\/(.*)/);
    if (match?.[1]) {
      return <Navigate to={`/register?form=${match[1]}`} />;
    }
  }

  if (isSubmitting) {
    return <Loading text="Logging in..." style="info" />;
  }

  return (
    <div className="ContactPage">
      <div className="container content">
        <Messages />
        <div className="bg-light-grey">
          <section>
            <header>
              <h3>{content.title}</h3>
            </header>
            {content.intro && <p>{content.intro}</p>}

            <Form
              onSubmit={onSubmit}
              render={({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="control-label" htmlFor="login_email">
                      Email
                    </label>
                    <Field
                      id="login_email"
                      name="email"
                      component="input"
                      type="text"
                      className="form-control text"
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label className="control-label" htmlFor="login_password">
                      Password
                    </label>
                    <Field
                      id="login_password"
                      name="password"
                      component="input"
                      type="password"
                      className="form-control text"
                      disabled={submitting}
                    />
                  </div>
                  <div className="Actions">
                    <button className="action btn btn-primary" type="submit" disabled={submitting}>
                      {content.buttonText}
                    </button>
                    <Link to="/reset-password" className="btn btn-link">
                      {content.forgotPasswordLinkText}
                    </Link>
                    {content.registerLinkUrl ? (
                      <a href={content.registerLinkUrl} className="btn btn-link">
                        {content.registerLinkText}
                      </a>
                    ) : null}
                  </div>
                </form>
              )}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  content: state.config.content.login,
});

const mapDispatchToProps = {
  loginSubmit,
  addMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
